import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  author?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  articleAuthor?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleTags?: string[];
}

export const useSEO = ({
  title,
  description,
  keywords,
  author,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  twitterTitle,
  twitterDescription,
  twitterImage,
  articleAuthor,
  articlePublishedTime,
  articleModifiedTime,
  articleTags
}: SEOProps) => {
  useEffect(() => {
    // Set document title
    if (title) {
      document.title = `${title} | Sera Blog`;
    }

    // Helper function to set or update meta tags
    const setMetaTag = (name: string, content: string, property?: boolean) => {
      const attribute = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };

    // Basic meta tags
    if (description) setMetaTag('description', description);
    if (keywords) setMetaTag('keywords', keywords);
    if (author) setMetaTag('author', author);

    // Canonical URL
    if (canonical) {
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.href = canonical;
    }

    // Open Graph tags
    if (ogTitle) setMetaTag('og:title', ogTitle, true);
    if (ogDescription) setMetaTag('og:description', ogDescription, true);
    if (ogImage) setMetaTag('og:image', ogImage, true);
    if (ogUrl) setMetaTag('og:url', ogUrl, true);
    setMetaTag('og:type', 'article', true);

    // Twitter Card tags
    if (twitterTitle) setMetaTag('twitter:title', twitterTitle, true);
    if (twitterDescription) setMetaTag('twitter:description', twitterDescription, true);
    if (twitterImage) setMetaTag('twitter:image', twitterImage, true);
    setMetaTag('twitter:card', 'summary_large_image', true);

    // Article-specific meta tags
    if (articleAuthor) setMetaTag('article:author', articleAuthor, true);
    if (articlePublishedTime) setMetaTag('article:published_time', articlePublishedTime, true);
    if (articleModifiedTime) setMetaTag('article:modified_time', articleModifiedTime, true);
    if (articleTags && articleTags.length > 0) {
      articleTags.forEach(tag => {
        setMetaTag('article:tag', tag, true);
      });
    }

    // Cleanup function to reset to default values when component unmounts
    return () => {
      document.title = 'Sera — Simple Batch Notifications';
      // Reset to default meta tags if needed
    };
  }, [title, description, keywords, author, canonical, ogTitle, ogDescription, ogImage, ogUrl, twitterTitle, twitterDescription, twitterImage, articleAuthor, articlePublishedTime, articleModifiedTime, articleTags]);
};
